import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, MagicWand, CircleNotch, DownloadSimple, ArrowsHorizontal, X } from "@phosphor-icons/react";
import { api, assetUrl, errDetail } from "../lib/api";
import { Sidebar } from "../components/Sidebar";

export default function Upscaler() {
  const [uploading, setUploading] = useState(false);
  const [source, setSource] = useState(null); // { id, name, mime, url, w, h }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { asset_id, url, w, h }
  const [dragging, setDragging] = useState(false);
  const [sliderPos, setSliderPos] = useState(50);
  const fileRef = useRef(null);
  const dragDepth = useRef(0);

  const loadDimensions = (url) =>
    new Promise((res) => {
      const img = new Image();
      img.onload = () => res({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => res({ w: 0, h: 0 });
      img.src = url;
    });

  const uploadOne = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      const previewUrl = URL.createObjectURL(file);
      const dims = await loadDimensions(previewUrl);
      setSource({ id: data.id, name: data.name, mime: data.mime, url: previewUrl, ...dims });
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
    const onEnter = (e) => { e.preventDefault(); if (hasFiles(e)) { dragDepth.current += 1; setDragging(true); } };
    const onOver = (e) => { if (hasFiles(e)) e.preventDefault(); };
    const onLeave = (e) => { e.preventDefault(); dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); } };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      const f = Array.from(e.dataTransfer.files || []).find((x) => x.type?.startsWith("image/"));
      if (f) uploadOne(f);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  const runUpscale = async () => {
    if (!source) return;
    setBusy(true);
    try {
      const { data } = await api.post("/upscale",
        { source_type: "file", source_id: source.id, scale: 2 },
        { timeout: 240000 });
      const url = assetUrl(data.asset_id);
      const dims = await loadDimensions(url);
      setResult({ asset_id: data.asset_id, url, ...dims });
      setSliderPos(50);
      toast.success(`Upscaled to ${dims.w}×${dims.h}. Drag the slider to compare.`);
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    if (source?.url?.startsWith("blob:")) URL.revokeObjectURL(source.url);
    setSource(null); setResult(null);
  };

  return (
    <div className="flex h-screen bg-[var(--bg)] overflow-hidden">
      <Sidebar />
      {dragging && (
        <div className="fixed inset-0 z-50 bg-[var(--bg)]/85 backdrop-blur-sm flex flex-col items-center justify-center gap-3 pointer-events-none">
          <span className="w-16 h-16 rounded-full flex items-center justify-center border-2 border-dashed"
                style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>
            <UploadSimple size={28} weight="bold" />
          </span>
          <p className="text-lg font-serif-display font-semibold text-[var(--text)]">Drop image to upscale</p>
        </div>
      )}
      <main className="flex-1 overflow-y-auto px-6 md:px-10 py-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-9 h-9 rounded-2xl flex items-center justify-center" style={{ background: "var(--accent)", color: "white" }}>
              <MagicWand size={18} weight="fill" />
            </span>
            <h1 className="font-serif-display font-semibold text-3xl sm:text-4xl tracking-tight">Upscaler</h1>
          </div>
          <p className="text-sm text-[var(--text-2)] mb-8 max-w-2xl">
            Pixel-perfect resolution boost powered by Real-ESRGAN. Composition stays identical to the source —
            only sharpness, edges, and fine texture are enhanced. Ideal for product photos, catalog images, and archival work.
          </p>

          {!source && (
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upscaler-picker"
              className="w-full h-64 flex flex-col items-center justify-center gap-3 border-2 border-dashed border-[var(--border-strong)] rounded-3xl text-[var(--text-2)] hover:border-[var(--accent)]/60 hover:text-[var(--text)] transition-colors duration-300 bg-[var(--surface)]">
              {uploading ? <CircleNotch size={28} className="animate-spin" /> : <UploadSimple size={32} weight="duotone" />}
              <span className="text-sm font-medium">{uploading ? "Uploading…" : "Drag & drop an image or click to browse"}</span>
              <span className="text-xs text-[var(--muted)]">PNG · JPEG · WebP · up to 20 MB</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" hidden data-testid="upscaler-file-input"
                 onChange={(e) => { uploadOne(e.target.files?.[0]); e.target.value = ""; }} />

          {source && (
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-5 space-y-5" style={{ boxShadow: "var(--shadow)" }}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[var(--text)] truncate" data-testid="upscaler-source-name">{source.name}</p>
                  <p className="text-xs text-[var(--muted)]">
                    Source: {source.w}×{source.h}
                    {result && ` → Upscaled: ${result.w}×${result.h}`}
                  </p>
                </div>
                <button data-testid="upscaler-run" onClick={runUpscale} disabled={busy || !source}
                  className="inline-flex items-center gap-2 rounded-full text-white font-semibold text-sm px-5 py-2 transition-opacity duration-300 disabled:opacity-50"
                  style={{ background: "var(--accent)" }}>
                  {busy ? <CircleNotch size={15} className="animate-spin" /> : <MagicWand size={15} weight="fill" />}
                  {busy ? "Upscaling…" : "Upscale"}
                </button>
                {result && (
                  <a href={`${result.url}${result.url.includes("?") ? "&" : "?"}download=1`} download data-testid="upscaler-download"
                    className="inline-flex items-center gap-1.5 rounded-full text-[var(--text)] font-semibold text-sm px-4 py-2 border border-[var(--border-strong)] hover:border-[var(--accent)]/60 transition-colors duration-300">
                    <DownloadSimple size={15} /> Download
                  </a>
                )}
                <button data-testid="upscaler-reset" onClick={reset} title="Start over"
                  className="text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--hover)] transition-colors duration-300 p-2 rounded-full">
                  <X size={16} />
                </button>
              </div>

              {/* Compare / Preview */}
              {!result ? (
                <div className="rounded-2xl overflow-hidden bg-[var(--surface-2)] border border-[var(--border)] flex items-center justify-center min-h-[300px]">
                  <img src={source.url} alt={source.name} className="max-h-[60vh] w-auto object-contain" data-testid="upscaler-preview-source" />
                </div>
              ) : (
                <div className="relative rounded-2xl overflow-hidden bg-[var(--surface-2)] border border-[var(--border)] select-none"
                     style={{ minHeight: 300 }} data-testid="upscaler-compare">
                  {/* Upscaled full underneath */}
                  <img src={result.url} alt="upscaled" className="block w-full h-auto object-contain" draggable={false} />
                  {/* Source clipped on top */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}>
                    <img src={source.url} alt="source" className="block w-full h-auto object-contain" draggable={false} />
                  </div>
                  {/* Handle */}
                  <div className="absolute inset-y-0" style={{ left: `${sliderPos}%`, transform: "translateX(-50%)" }}>
                    <div className="h-full w-0.5 bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.5)]" />
                    <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-0 w-8 h-8 rounded-full bg-white flex items-center justify-center text-black shadow-lg">
                      <ArrowsHorizontal size={14} weight="bold" />
                    </span>
                  </div>
                  {/* Labels */}
                  <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-black/60 text-white px-2 py-1 rounded-md">Before</span>
                  <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider bg-black/60 text-white px-2 py-1 rounded-md">After · 2x</span>
                  <input type="range" min={0} max={100} value={sliderPos} onChange={(e) => setSliderPos(Number(e.target.value))}
                         data-testid="upscaler-slider"
                         className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize" />
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
